import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

/** Unwrap DynamoDB attribute or plain value */
const dynS = (v, fallback = "") => {
  if (v == null) return fallback;
  if (typeof v === "object" && "S" in v) return v.S ?? fallback;
  if (typeof v === "object" && "NULL" in v) return fallback;
  return v === "" ? fallback : String(v);
};

const dynN = (v, fallback = "N/A") => {
  if (v == null) return fallback;
  if (typeof v === "object" && "N" in v) return v.N ?? fallback;
  if (typeof v === "object" && "NULL" in v) return fallback;
  return String(v);
};

const dynBool = (v) => {
  if (v == null) return false;
  if (typeof v === "object" && "BOOL" in v) return Boolean(v.BOOL);
  if (typeof v === "boolean") return v;
  if (v === "Yes" || v === "true" || v === "1") return true;
  return false;
};

const yesNo = (bool) => (bool ? "Yes" : "No");

/**
 * Format a raw call record from get-calls into a flat row for the dashboard table.
 */
export const formatCall = (call) => {
  const callId = dynS(call.callId, "");
  const hasVm =
    dynBool(call.has_vm) || call.voicemail === "Yes" || call.voicemail === true;

  return {
    callTime: dynN(call.startTime, "N/A"),
    toNumber: dynS(call.toNumber, "N/A"),
    callerNumber: dynS(call.fromNumber, "N/A"),
    action: dynS(call.action, "N/A"),
    actionDetails: dynS(call.action_details ?? call.actionDetails, "N/A"),
    voicemail: yesNo(hasVm),
    voicemailDropped: dynBool(call.voicemail_dropped),
    callUser: dynS(call.call_user ?? call.callUser, ""),
    hasRecording: yesNo(dynBool(call.has_recording)),
    hasTranscript: yesNo(dynBool(call.has_transcript)),
    route: dynS(call.route, ""),
    isAfterHours: yesNo(dynBool(call.isAfterHours)),
    notification: dynS(call.notification, ""),
    notificationDetails: dynS(call.notificationDetails, ""),
    callId,
    callSid: dynS(call.callSid ?? call.call_sid, ""),
    contactName: dynS(call.contactName ?? call.contact_name, ""),
    contactCompany: dynS(call.contactCompany ?? call.contact_company, ""),
    link: "/dashboard/call-details/" + callId,
    // Keep legacy key for any leftover consumers
    call_user: dynS(call.call_user ?? call.callUser, ""),
  };
};

export const calculateDateRange = (days) => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - days);
  return {
    startDate: startDate.toISOString().split("T")[0],
    endDate: endDate.toISOString().split("T")[0],
  };
};

const getTimestampInTimezone = (dateStr, hour, minute, second, orgTimezone) => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: orgTimezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const dateTimeStr = `${dateStr}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:${String(second).padStart(2, "0")}`;
  let testDate = new Date(dateTimeStr);
  let attempts = 0;
  const maxAttempts = 5;

  while (attempts < maxAttempts) {
    const parts = formatter.formatToParts(testDate);
    const testYear = parts.find((p) => p.type === "year").value;
    const testMonth = parts.find((p) => p.type === "month").value;
    const testDay = parts.find((p) => p.type === "day").value;
    const testHour = parts.find((p) => p.type === "hour").value;
    const testMinute = parts.find((p) => p.type === "minute").value;
    const testSecond = parts.find((p) => p.type === "second").value;

    const [targetYear, targetMonth, targetDay] = dateStr.split("-");

    if (
      testYear === targetYear &&
      testMonth === targetMonth &&
      testDay === targetDay &&
      testHour === String(hour).padStart(2, "0") &&
      testMinute === String(minute).padStart(2, "0") &&
      testSecond === String(second).padStart(2, "0")
    ) {
      return Math.floor(testDate.getTime() / 1000);
    }

    const hourDiff = hour - parseInt(testHour, 10);
    const minuteDiff = minute - parseInt(testMinute, 10);
    const secondDiff = second - parseInt(testSecond, 10);
    const totalSecondsDiff = hourDiff * 3600 + minuteDiff * 60 + secondDiff;

    testDate = new Date(testDate.getTime() + totalSecondsDiff * 1000);
    attempts++;
  }

  return Math.floor(testDate.getTime() / 1000);
};

/**
 * Resolve Unix start/end (seconds) for a YYYY-MM-DD range in org timezone.
 */
const resolveTimeBounds = (startDate, endDate) => {
  const orgData = JSON.parse(localStorage.getItem("organization") || "{}");
  const orgTimezone =
    orgData?.org_timezone?.S ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const startDateStr = startDate.split("T")[0];
  const startTime = getTimestampInTimezone(startDateStr, 0, 0, 0, orgTimezone);

  let endTime;
  if (endDate) {
    const endDateStr = endDate.split("T")[0];
    endTime = getTimestampInTimezone(endDateStr, 23, 59, 59, orgTimezone);
  } else {
    endTime = Math.floor(Date.now() / 1000);
  }

  return { startTime, endTime };
};

const fetchCallsForRange = async (startDate, endDate) => {
  const { startTime, endTime } = resolveTimeBounds(startDate, endDate);
  const orgId = JSON.parse(localStorage.getItem("organization")).orgId.S;
  const response = await axios.post(
    process.env.REACT_APP_BACKEND_URL + "call/get-calls",
    {
      organization_id: orgId,
      start_time: startTime,
      end_time: endTime,
    }
  );
  return (response.data.calls || []).map(formatCall);
};

/** Fixed window for KPI cards: L7D + prior week + 4 weekly sparkline buckets */
export const METRICS_LOOKBACK_DAYS = 30;

/** True when table range fully contains the metrics lookback window */
export const rangeCoversMetricsWindow = (startDate, endDate) => {
  if (!startDate || !endDate) return false;
  const metrics = calculateDateRange(METRICS_LOOKBACK_DAYS);
  return (
    String(startDate).split("T")[0] <= metrics.startDate &&
    String(endDate).split("T")[0] >= metrics.endDate
  );
};

/**
 * Dashboard call list data: fetch, date presets, loading/error.
 * Also loads a dedicated last-30-days set for metric cards (independent of table filters).
 * When the table range already covers that window, metricCalls reuse the same payload.
 */
export default function useDashboardCalls(initialDays = 30) {
  const location = useLocation();
  const [errMsg, setErrorMsg] = useState(null);
  // Start true so the first paint shows loading, not the empty state
  const [loader, setLoader] = useState(true);
  const [calls, setCalls] = useState([]);
  const [metricCalls, setMetricCalls] = useState([]);
  const [metricsLoader, setMetricsLoader] = useState(true);
  const [metricsDateRange, setMetricsDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [dateRange, setDateRange] = useState(initialDays);
  const [callDateRange, setCallDateRange] = useState({
    startDate: null,
    endDate: null,
  });

  const isFetchingRef = useRef(false);
  const lastRequestParamsRef = useRef(null);
  // Latest range requested while a fetch is in flight; replayed when the lock releases.
  const pendingRangeRef = useRef(null);
  const isInitialMountRef = useRef(true);
  const hasLoadedInThisCycleRef = useRef(false);
  const isMetricsFetchingRef = useRef(false);
  const metricsReadyRef = useRef(false);
  const dateRangeRef = useRef(dateRange);
  dateRangeRef.current = dateRange;

  const getMetricCalls = useCallback(async () => {
    if (isMetricsFetchingRef.current) return;
    if (metricsReadyRef.current) return;

    isMetricsFetchingRef.current = true;
    const { startDate, endDate } = calculateDateRange(METRICS_LOOKBACK_DAYS);
    setMetricsDateRange({ startDate, endDate });
    setMetricsLoader(true);
    try {
      const formatted = await fetchCallsForRange(startDate, endDate);
      setMetricCalls(formatted);
      metricsReadyRef.current = true;
    } catch (error) {
      console.error("Failed to load metric calls:", error);
      // Don't overwrite table error with metrics failure; leave prior metricCalls
    } finally {
      setMetricsLoader(false);
      isMetricsFetchingRef.current = false;
    }
  }, []);

  const getCalls = useCallback(
    async (startDate = null, endDate = null) => {
      if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
        setLoader(false);
        isFetchingRef.current = false;
        return;
      }

      const requestKey = `${startDate || "default"}_${endDate || "default"}_${dateRangeRef.current}`;

      if (isFetchingRef.current) {
        if (lastRequestParamsRef.current === requestKey) {
          // Duplicate of the in-flight request (e.g. Strict Mode); drop any stale pending.
          pendingRangeRef.current = null;
          return;
        }
        pendingRangeRef.current = { startDate, endDate };
        setLoader(true);
        return;
      }

      isFetchingRef.current = true;
      lastRequestParamsRef.current = requestKey;
      setLoader(true);
      setErrorMsg(null);

      const replayPendingRange = () => {
        const pending = pendingRangeRef.current;
        if (!pending) return false;
        pendingRangeRef.current = null;
        isFetchingRef.current = false;
        getCalls(pending.startDate, pending.endDate);
        return true;
      };

      const reusePayloadForMetrics = (formatted, rangeStart, rangeEnd) => {
        if (!rangeCoversMetricsWindow(rangeStart, rangeEnd)) return;
        setMetricsDateRange(calculateDateRange(METRICS_LOOKBACK_DAYS));
        setMetricCalls(formatted);
        metricsReadyRef.current = true;
        setMetricsLoader(false);
      };

      try {
        let rangeStart = startDate;
        let rangeEnd = endDate;

        if (!rangeStart) {
          const calculated = calculateDateRange(dateRangeRef.current || 30);
          rangeStart = calculated.startDate;
          rangeEnd = calculated.endDate;
        }

        const formatted = await fetchCallsForRange(rangeStart, rangeEnd);

        if (replayPendingRange()) {
          // Picker already moved; don't paint stale table rows. KPI cards can
          // still reuse this payload when it covers the metrics window.
          reusePayloadForMetrics(formatted, rangeStart, rangeEnd);
          return;
        }

        setCalls(formatted);
        reusePayloadForMetrics(formatted, rangeStart, rangeEnd);

        if (
          !rangeCoversMetricsWindow(rangeStart, rangeEnd) &&
          !metricsReadyRef.current
        ) {
          // Narrower table range and no metrics yet — fetch metrics window once
          // Release table fetch lock first so UI can settle; metrics has its own guard
          isFetchingRef.current = false;
          setLoader(false);
          await getMetricCalls();
          return;
        }

        setLoader(false);
        isFetchingRef.current = false;
      } catch (error) {
        if (replayPendingRange()) {
          return;
        }

        const status = error?.response?.status;
        const apiError = error?.response?.data?.error;
        const apiMessage = error?.response?.data?.message;

        if (
          status === 401 &&
          typeof apiError === "string" &&
          apiError.toLowerCase().includes("authorization header")
        ) {
          setErrorMsg(
            "We ran into an authentication issue and can’t verify your session. Please log out and log back in."
          );
        } else if (error.response && error.response.data) {
          setErrorMsg([apiError, apiMessage].filter(Boolean).join(" "));
        } else {
          setErrorMsg("Failed to load calls.");
        }
        setLoader(false);
        setMetricsLoader(false);
        isFetchingRef.current = false;
      }
    },
    [getMetricCalls]
  );

  const handleDateRangeChange = useCallback(
    (startDate, endDate) => {
      if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
        return;
      }
      setDateRange(null); // custom range — not a preset
      setCallDateRange({ startDate, endDate });
      getCalls(startDate, endDate);
    },
    [getCalls]
  );

  const handlePresetChange = useCallback(
    (days) => {
      setDateRange(days);
      if (isInitialMountRef.current) return;
      const { startDate, endDate } = calculateDateRange(days);
      setCallDateRange({ startDate, endDate });
      getCalls(startDate, endDate);
    },
    [getCalls]
  );

  // Initial load + remount on navigation
  useEffect(() => {
    if (hasLoadedInThisCycleRef.current) {
      return;
    }

    metricsReadyRef.current = false;

    const { startDate, endDate } = calculateDateRange(dateRangeRef.current);
    setCallDateRange({ startDate, endDate });
    // Single fetch when default range covers metrics (e.g. 30 days);
    // getCalls will call getMetricCalls only if the table window is narrower.
    getCalls(startDate, endDate);

    hasLoadedInThisCycleRef.current = true;
    isInitialMountRef.current = false;

    const timeoutId = setTimeout(() => {
      hasLoadedInThisCycleRef.current = false;
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      hasLoadedInThisCycleRef.current = false;
      isInitialMountRef.current = true;
      metricsReadyRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return {
    calls,
    metricCalls,
    metricsLoader,
    metricsDateRange,
    loader,
    errMsg,
    dateRange,
    setDateRange: handlePresetChange,
    callDateRange,
    handleDateRangeChange,
    getCalls,
    getMetricCalls,
  };
}
